import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  COMMUNITY_DEV_MODE_STORAGE_KEY,
  COMMUNITY_PREVIEW_AS_USER_KEY,
  getMarketProfileDevPreset,
  isCommunityDevUser,
  type MarketProfileDevPreset,
} from './communityDevMode';
import { applyDevMarketProfilePreset } from './marketProfile/marketProfileStore';

type CommunityDevModeContextValue = {
  isDevUser: boolean;
  devMode: boolean;
  previewAsUser: boolean;
  marketProfilePreset: MarketProfileDevPreset | null;
  marketProfileRevision: number;
  effectiveIsAdmin: (realIsAdmin: boolean) => boolean;
  toggleDevMode: () => void;
  setPreviewAsUser: (value: boolean) => void;
  applyMarketProfilePreset: (preset: MarketProfileDevPreset) => void;
};

const CommunityDevModeContext = createContext<CommunityDevModeContextValue | null>(null);

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function CommunityDevModeProvider({
  children,
  userEmail,
}: {
  children: React.ReactNode;
  userEmail: string | null | undefined;
}) {
  const isDevUser = isCommunityDevUser(userEmail);
  const [devMode, setDevModeState] = useState(false);
  const [previewAsUser, setPreviewAsUserState] = useState(false);
  const [marketProfilePreset, setMarketProfilePresetState] = useState<MarketProfileDevPreset | null>(null);
  const [marketProfileRevision, setMarketProfileRevision] = useState(0);

  useEffect(() => {
    if (!isDevUser) {
      setDevModeState(false);
      setPreviewAsUserState(false);
      setMarketProfilePresetState(null);
      return;
    }
    setDevModeState(readFlag(COMMUNITY_DEV_MODE_STORAGE_KEY));
    setPreviewAsUserState(readFlag(COMMUNITY_PREVIEW_AS_USER_KEY));
    setMarketProfilePresetState(getMarketProfileDevPreset());
  }, [isDevUser, userEmail]);

  const setPreviewAsUser = useCallback((value: boolean) => {
    setPreviewAsUserState(value);
    writeFlag(COMMUNITY_PREVIEW_AS_USER_KEY, value);
  }, []);

  const toggleDevMode = useCallback(() => {
    setDevModeState((prev) => {
      const next = !prev;
      writeFlag(COMMUNITY_DEV_MODE_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const applyMarketProfilePreset = useCallback((preset: MarketProfileDevPreset) => {
    if (!isCommunityDevUser(userEmail) || !userEmail) return;
    applyDevMarketProfilePreset(userEmail, preset);
    setMarketProfilePresetState(preset);
    setMarketProfileRevision((r) => r + 1);
  }, [userEmail]);

  const effectiveIsAdmin = useCallback(
    (realIsAdmin: boolean) => realIsAdmin && !previewAsUser,
    [previewAsUser],
  );

  const value = useMemo(
    (): CommunityDevModeContextValue => ({
      isDevUser,
      devMode: isDevUser && devMode,
      previewAsUser: isDevUser && previewAsUser,
      marketProfilePreset: isDevUser ? marketProfilePreset : null,
      marketProfileRevision: isDevUser ? marketProfileRevision : 0,
      effectiveIsAdmin,
      toggleDevMode,
      setPreviewAsUser,
      applyMarketProfilePreset,
    }),
    [
      isDevUser,
      devMode,
      previewAsUser,
      marketProfilePreset,
      marketProfileRevision,
      effectiveIsAdmin,
      toggleDevMode,
      setPreviewAsUser,
      applyMarketProfilePreset,
    ],
  );

  return (
    <CommunityDevModeContext.Provider value={value}>
      {children}
    </CommunityDevModeContext.Provider>
  );
}

export function useCommunityDevMode() {
  const ctx = useContext(CommunityDevModeContext);
  if (!ctx) throw new Error('useCommunityDevMode must be used within CommunityDevModeProvider');
  return ctx;
}
